import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, BarChart3, Globe } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";

const TROPHY = "https://images.unsplash.com/photo-1567427017947-545c5f8d16ad?w=600&q=80&auto=format&fit=crop";
const MIC = "https://images.unsplash.com/photo-1478737270239-2f02b77fc618?w=600&q=80&auto=format&fit=crop";

export function GetVerifiedCard() {
  return (
    <div data-testid="promo-get-verified" className="relative overflow-hidden rounded-md bg-bordeaux text-white p-7 min-h-[220px] flex items-center">
      <img src={MIC} alt="" className="absolute right-0 top-0 h-full w-1/2 object-cover opacity-40 mix-blend-luminosity" />
      <div className="absolute inset-0 bg-gradient-to-r from-bordeaux via-bordeaux/90 to-bordeaux/30" />
      <div className="relative flex gap-5 items-start">
        <ShieldCheck className="w-14 h-14 text-gold shrink-0" strokeWidth={1.3} />
        <div>
          <h3 className="font-serif text-2xl font-semibold">Get Verified</h3>
          <p className="text-sm text-white/85 mt-2 max-w-xs">Build a trusted and internationally recognised professional profile.</p>
          <Link to="/get-verified" data-testid="promo-verification-btn" className="btn-gold btn-sm mt-5">Start Verification <ArrowRight className="w-3.5 h-3.5" /></Link>
        </div>
      </div>
    </div>
  );
}

export function RankingsCard() {
  return (
    <div data-testid="promo-rankings" className="relative overflow-hidden rounded-md bg-white border border-[#E6E3DC] p-7 min-h-[220px] flex items-center">
      <img src={TROPHY} alt="Trophy" className="absolute right-0 top-0 h-full w-[45%] object-cover" />
      <div className="absolute inset-0 bg-gradient-to-r from-white via-white/95 to-white/10" />
      <div className="relative flex gap-5 items-start">
        <BarChart3 className="w-14 h-14 text-gold shrink-0" strokeWidth={1.3} />
        <div>
          <h3 className="font-serif text-2xl font-semibold text-ink">WEI Rankings</h3>
          <p className="text-sm text-charcoal mt-2 max-w-xs">Evidence-based classifications of the world’s entertainment talent, organisations, institutions, venues and works.</p>
          <Link to="/rankings" data-testid="promo-rankings-btn" className="btn-outline btn-sm mt-5">View Rankings <ArrowRight className="w-3.5 h-3.5" /></Link>
        </div>
      </div>
    </div>
  );
}

export function IntelligenceCard() {
  return (
    <div data-testid="promo-intelligence" className="relative overflow-hidden rounded-md bg-ink text-white p-7 min-h-[220px] flex items-center">
      <svg className="absolute -right-10 -bottom-16 w-72 h-72 opacity-60" viewBox="0 0 200 200" fill="none" stroke="#C99738" strokeWidth="0.6">
        <circle cx="100" cy="100" r="90" /><ellipse cx="100" cy="100" rx="40" ry="90" /><ellipse cx="100" cy="100" rx="70" ry="90" /><line x1="10" y1="100" x2="190" y2="100" /><path d="M20 60 Q100 40 180 60" /><path d="M20 140 Q100 160 180 140" />
        {[[40, 70], [130, 50], [160, 120], [70, 150], [110, 100]].map(([x, y]) => <circle key={x} cx={x} cy={y} r="2" fill="#E8C879" stroke="none" />)}
      </svg>
      <div className="relative flex gap-5 items-start">
        <Globe className="w-14 h-14 text-gold shrink-0" strokeWidth={1.3} />
        <div>
          <h3 className="font-serif text-2xl font-semibold">WEI Intelligence</h3>
          <p className="text-sm text-white/75 mt-2 max-w-xs">Data, research and insights for the global entertainment industry.</p>
          <Link to="/intelligence" data-testid="promo-intelligence-btn" className="btn-gold btn-sm mt-5">Explore Intelligence <ArrowRight className="w-3.5 h-3.5" /></Link>
        </div>
      </div>
    </div>
  );
}

export function PromoCards() {
  return (
    <section className="container-wei py-10 grid md:grid-cols-3 gap-5" data-testid="promo-cards">
      <GetVerifiedCard /><RankingsCard /><IntelligenceCard />
    </section>
  );
}

const PARTNERS = ["Africa Music Honours", "Meridian Trust", "Global Music Summit", "Latin Screen Awards", "European Dance Honours", "Toronto Screen Forum"];

export function Partners() {
  return (
    <section data-testid="partners-section" className="border-y border-[#E6E3DC] bg-white">
      <div className="container-wei py-8">
        <p className="eyebrow text-[#8A867E] text-center mb-5">Institutional partners & data contributors (demo)</p>
        <div className="flex flex-wrap justify-center gap-x-10 gap-y-3">{PARTNERS.map((p) => <span key={p} className="font-serif text-lg text-charcoal/70 hover:text-ink transition-colors">{p}</span>)}</div>
      </div>
    </section>
  );
}

export function Newsletter() {
  const [email, setEmail] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/newsletter", { email }); toast.success("Subscribed to the WEI Record briefing"); setEmail(""); } catch (err) { toast.error(errorMessage(err)); }
  };
  return (
    <section data-testid="newsletter-section" className="container-wei py-14">
      <div className="grid md:grid-cols-2 gap-8 items-center">
        <div>
          <div className="eyebrow text-gold mb-3">The WEI Briefing</div>
          <h2 className="font-serif text-3xl font-semibold text-ink">Receive the entertainment record, weekly.</h2>
          <p className="text-charcoal mt-3 text-sm">Rankings movement, verification announcements, editorial and intelligence — delivered every Monday.</p>
        </div>
        <form onSubmit={submit} className="flex gap-2">
          <input data-testid="newsletter-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email address" className="input-wei" />
          <button data-testid="newsletter-submit-btn" className="btn-bordeaux">Subscribe</button>
        </form>
      </div>
    </section>
  );
}
